import { useState, useEffect, useRef, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuthStore } from '../../stores/authStore'
import { useNavigate } from 'react-router-dom'
import GovLayout from '../../components/layout/GovLayout'
import {
  MessageCircle, Share2, Bookmark, MoreHorizontal, Send,
  Image as ImageIcon, Video, Smile, X, Loader2, Link2, Check, Trash2, Repeat, Flag
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '../../lib/utils'
import postService, { Post, Comment } from '../../services/postService'
import { getApiErrorMessage } from '../../services/api'
import EmojiPicker from 'emoji-picker-react'
import ReportModal from '@/components/common/ReportModal'

// All 7 Facebook-style reactions
const REACTIONS = [
  { type: 'LIKE', icon: '👍', color: '#1877F2', label: 'Like' },
  { type: 'LOVE', icon: '❤️', color: '#F5533D', label: 'Love' },
  { type: 'CARE', icon: '🤗', color: '#FF69B4', label: 'Care' },
  { type: 'HAHA', icon: '😂', color: '#F7B125', label: 'Haha' },
  { type: 'WOW', icon: '😲', color: '#F7B125', label: 'Wow' },
  { type: 'SAD', icon: '😢', color: '#F7B125', label: 'Sad' },
  { type: 'ANGRY', icon: '😡', color: '#E9710F', label: 'Angry' },
]

const FeedPage = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [hasMore, setHasMore] = useState(true)
  const [page, setPage] = useState(0)
  const [createPostContent, setCreatePostContent] = useState('')
  const [createPostMedia, setCreatePostMedia] = useState<File | null>(null)
  const [createPostMediaPreview, setCreatePostMediaPreview] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [activeCommentPost, setActiveCommentPost] = useState<number | null>(null)
  const [comments, setComments] = useState<{ [postId: number]: Comment[] }>({})
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [showReactionPicker, setShowReactionPicker] = useState<number | null>(null)
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [_savingPosts, setSavingPosts] = useState<Set<number>>(new Set())
  const [showShareMenu, setShowShareMenu] = useState<number | null>(null)
  const [copiedPostId, setCopiedPostId] = useState<number | null>(null)
  const [expandedReplies, setExpandedReplies] = useState<Set<number>>(new Set())
  const [postMenuOpen, setPostMenuOpen] = useState<number | null>(null)
  const [deletingPost, setDeletingPost] = useState(false)
  const [reportPostId, setReportPostId] = useState<number | null>(null)

  const observerRef = useRef<IntersectionObserver | null>(null)
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  // Load posts
  const loadPosts = useCallback(async (pageNum: number = 0) => {
    if (pageNum === 0) setLoading(true)
    try {
      const response = await postService.getFeedPosts(pageNum, 20)
      if (response.success) {
        const newPosts = response.data?.content || response.data || []
        if (pageNum === 0) {
          setPosts(newPosts)
        } else {
          setPosts(prev => [...prev, ...newPosts])
        }
        setHasMore(response.data?.hasNext || newPosts.length === 20)
      }
    } catch (error) {
      console.error('Error loading posts:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPosts(0)
  }, [loadPosts])

  // Infinite scroll
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prev => prev + 1)
          loadPosts(page + 1)
        }
      },
      { threshold: 0.5 }
    )

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current)
    }

    return () => observerRef.current?.disconnect()
  }, [hasMore, loading, page, loadPosts])

  // Create post
  const handleCreatePost = async () => {
    if (!createPostContent.trim() && !createPostMedia) return
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    setCreating(true)
    try {
      let mediaId: number | undefined
      if (createPostMedia) {
        const uploadResponse = await postService.uploadMedia(createPostMedia)
        if (uploadResponse.success) {
          mediaId = uploadResponse.data?.id
        }
      }

      const response = await postService.createPost({
        content: createPostContent,
        postType: createPostMedia?.type.startsWith('video') ? 'VIDEO' : 'IMAGE',
        mediaIds: mediaId ? [mediaId] : undefined
      })

      if (response.success) {
        setPosts(prev => [response.data, ...prev])
        setCreatePostContent('')
        setCreatePostMedia(null)
        setCreatePostMediaPreview(null)
      }
    } catch (error) {
      console.error('Error creating post:', error)
      alert(getApiErrorMessage(error, 'Failed to create post. Please try again.'))
    } finally {
      setCreating(false)
    }
  }

  // Handle media selection
  const handleMediaSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 20 * 1024 * 1024) {
        const mb = (file.size / (1024 * 1024)).toFixed(1)
        alert(`This file is ${mb} MB. The maximum allowed upload is 20 MB. Please choose a smaller file.`)
        e.target.value = ''
        return
      }
      setCreatePostMedia(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCreatePostMediaPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // Handle reaction
  const handleReaction = async (postId: number, reactionType: string) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    try {
      const response = await postService.react(postId, reactionType)
      if (response.success) {
        setPosts(prev => prev.map(post => {
          if (post.id === postId) {
            const currentReaction = post.reactionType
            const reactionCounts = { ...(post.reactionCounts || {}) }

            // Remove old reaction
            if (currentReaction && reactionCounts[currentReaction]) {
              reactionCounts[currentReaction] = Math.max(0, (reactionCounts[currentReaction] || 1) - 1)
            }

            // Add new reaction (or toggle off)
            if (currentReaction === reactionType) {
              return { ...post, reactionType: undefined, reactionCounts, isLiked: false }
            }

            reactionCounts[reactionType] = (reactionCounts[reactionType] || 0) + 1
            return { ...post, reactionType: reactionType as any, reactionCounts, isLiked: true }
          }
          return post
        }))
      }
    } catch (error) {
      console.error('Error reacting to post:', error)
    }
    setShowReactionPicker(null)
  }

  // Handle like (simple toggle)
  const handleLike = async (postId: number) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    try {
      await postService.toggleLike(postId)
      setPosts(prev => prev.map(post =>
        post.id === postId
          ? { ...post, isLiked: !post.isLiked, likeCount: post.isLiked ? post.likeCount - 1 : post.likeCount + 1 }
          : post
      ))
    } catch (error) {
      console.error('Error liking post:', error)
    }
  }

  // Handle save/bookmark
  const handleSave = async (postId: number) => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    setSavingPosts(prev => new Set(prev).add(postId))
    try {
      await postService.toggleSave(postId)
      setPosts(prev => prev.map(post =>
        post.id === postId ? { ...post, isSaved: !post.isSaved } : post
      ))
    } catch (error) {
      console.error('Error saving post:', error)
    } finally {
      setSavingPosts(prev => { const newSet = new Set(prev); newSet.delete(postId); return newSet })
    }
  }

  // Handle share
  const handleShare = async (postId: number) => {
    try {
      await postService.sharePost(postId)
      setPosts(prev => prev.map(post =>
        post.id === postId ? { ...post, shareCount: post.shareCount + 1 } : post
      ))
    } catch (error) {
      console.error('Error sharing post:', error)
    }
  }

  // Handle reshare (idempotent toggle)
  const handleReshare = async (postId: number) => {
    const post = posts.find(p => p.id === postId)
    const wasReshared = post?.isReshared || false
    try {
      const res = wasReshared ? await postService.unresharePost(postId) : await postService.resharePost(postId)
      const updated = res?.data
      setPosts(prev => prev.map(p =>
        p.id === postId
          ? { ...p, isReshared: updated?.isReshared ?? !wasReshared, reshareCount: updated?.reshareCount ?? p.reshareCount }
          : p
      ))
    } catch (error) {
      console.error('Error resharing post:', error)
    }
  }

  // Delete own post
  const handleDeletePost = async (postId: number) => {
    if (!window.confirm('Delete this post permanently? This cannot be undone.')) return
    setPostMenuOpen(null)
    setDeletingPost(true)
    try {
      await postService.deletePost(postId)
      setPosts(prev => prev.filter(post => post.id !== postId))
    } catch (error) {
      console.error('Error deleting post:', error)
      alert('Could not delete the post. Please try again.')
    } finally {
      setDeletingPost(false)
    }
  }

  // Copy post link
  const handleCopyLink = (postId: number) => {
    const link = `${window.location.origin}/post/${postId}`
    navigator.clipboard.writeText(link).then(() => {
      setCopiedPostId(postId)
      setTimeout(() => setCopiedPostId(null), 2000)
    })
  }

  // Toggle replies visibility
  const toggleReplies = async (commentId: number, postId: number) => {
    const newExpanded = new Set(expandedReplies)
    if (newExpanded.has(commentId)) {
      newExpanded.delete(commentId)
    } else {
      newExpanded.add(commentId)
      // Fetch replies if not already loaded
      try {
        const response = await postService.getReplies(postId, commentId)
        if (response.success && response.data) {
          setComments(prev => ({
            ...prev,
            [postId]: prev[postId].map(c =>
              c.id === commentId ? { ...c, replies: response.data } : c
            )
          }))
        }
      } catch (error) {
        console.error('Error fetching replies:', error)
      }
    }
    setExpandedReplies(newExpanded)
  }

  // Handle comment
  const handleAddComment = async (postId: number) => {
    if (!commentText.trim() || !isAuthenticated) return

    try {
      const response = await postService.addComment(postId, commentText)
      if (response.success) {
        setComments(prev => ({
          ...prev,
          [postId]: [response.data, ...(prev[postId] || [])]
        }))
        setPosts(prev => prev.map(post =>
          post.id === postId
            ? { ...post, commentCount: post.commentCount + 1 }
            : post
        ))
        setCommentText('')
      }
    } catch (error) {
      console.error('Error adding comment:', error)
    }
  }

  // Fetch comments for a post
  const fetchComments = async (postId: number) => {
    if (comments[postId]) return // Already loaded
    setCommentsLoading(true)
    try {
      const response = await postService.getComments(postId)
      if (response.success) {
        setComments(prev => ({
          ...prev,
          [postId]: response.data?.content || []
        }))
      }
    } catch (error) {
      console.error('Error fetching comments:', error)
    } finally {
      setCommentsLoading(false)
    }
  }

  // Handle comment button click
  const handleCommentClick = (postId: number) => {
    if (activeCommentPost === postId) {
      setActiveCommentPost(null)
    } else {
      setActiveCommentPost(postId)
      fetchComments(postId)
    }
  }

  // Format time ago
  const formatTimeAgo = (dateString: string) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true })
  }

  // Get reaction display
  const getReactionDisplay = (post: Post) => {
    if (post.reactionType) {
      const reaction = REACTIONS.find(r => r.type === post.reactionType)
      return reaction ? { icon: reaction.icon, label: reaction.label } : { icon: '👍', label: 'Like' }
    }
    return post.isLiked ? { icon: '👍', label: 'Liked' } : { icon: '👍', label: 'Like' }
  }

  return (
    <GovLayout>
    <div className="max-w-2xl mx-auto py-6 px-4">
      {/* Create Post */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-4 mb-6"
      >
        <div className="flex gap-4">
          <img
            src={user?.profileImage || '/default-avatar.svg'}
            alt={user?.firstName}
            className="w-12 h-12 rounded-full object-cover bg-gradient-to-br from-emerald-400 to-teal-500"
          />
          <div className="flex-1">
            <textarea
              placeholder="Share your thoughts about TB awareness..."
              value={createPostContent}
              onChange={(e) => setCreatePostContent(e.target.value)}
              className="w-full resize-none border-0 bg-transparent focus:outline-none placeholder:text-muted-foreground"
              rows={3}
            />

            {/* Media Preview */}
            <AnimatePresence>
              {createPostMediaPreview && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  className="relative mb-3"
                >
                  {createPostMedia?.type.startsWith('video') ? (
                    <video src={createPostMediaPreview} controls className="max-h-64 rounded-lg" />
                  ) : (
                    <img src={createPostMediaPreview} alt="Preview" className="max-h-64 rounded-lg" />
                  )}
                  <button
                    onClick={() => { setCreatePostMedia(null); setCreatePostMediaPreview(null) }}
                    className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex items-center justify-between mt-2 pt-2 border-t">
              <div className="flex gap-2">
                <label className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer">
                  <input type="file" accept="image/*,video/*" className="hidden" onChange={handleMediaSelect} />
                  <ImageIcon className="w-5 h-5 text-emerald-500" />
                </label>
                <label className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer">
                  <input type="file" accept="video/*" className="hidden" onChange={handleMediaSelect} />
                  <Video className="w-5 h-5 text-blue-500" />
                </label>
                <button
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                >
                  <Smile className="w-5 h-5 text-yellow-500" />
                </button>
              </div>
              <button
                onClick={handleCreatePost}
                disabled={creating || (!createPostContent.trim() && !createPostMedia)}
                className="btn-primary flex items-center gap-2 disabled:opacity-50"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Post
              </button>
            </div>

            {/* Emoji Picker */}
            <AnimatePresence>
              {showEmojiPicker && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute z-10 mt-2"
                >
                  <EmojiPicker
                    onEmojiClick={(emojiData) => {
                      setCreatePostContent(prev => prev + emojiData.emoji)
                      setShowEmojiPicker(false)
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>

      {/* Feed */}
      <div className="space-y-4">
        {loading && posts.length === 0 ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-emerald-500" />
            <p className="mt-2 text-muted-foreground">Loading posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">No posts yet. Be the first to share!</p>
          </div>
        ) : (
          posts.map((post, index) => {
            const reactionDisplay = getReactionDisplay(post)
            return (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-card overflow-hidden"
              >
                {/* Post Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={post.user.profileImage || '/default-avatar.svg'}
                      alt={post.user.firstName}
                      className="w-10 h-10 rounded-full object-cover bg-gradient-to-br from-emerald-400 to-teal-500"
                    />
                    <div>
                      <p className="font-medium">{post.user.firstName} {post.user.lastName}</p>
                      <p className="text-sm text-muted-foreground">
                        @{post.user.username} · {formatTimeAgo(post.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <button
                      onClick={() => setPostMenuOpen(postMenuOpen === post.id ? null : post.id)}
                      className="p-2 hover:bg-muted rounded-full"
                      aria-label="Post options"
                    >
                      <MoreHorizontal className="w-5 h-5 text-muted-foreground" />
                    </button>
                    <AnimatePresence>
                      {postMenuOpen === post.id && (
                        <motion.div
                          initial={{ opacity: 0, y: -6 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -6 }}
                          className="absolute right-0 top-full mt-1 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-1 min-w-[160px] z-30 border dark:border-gray-700"
                        >
                          {user && post.user.id === user.id ? (
                            <button
                              onClick={() => handleDeletePost(post.id)}
                              disabled={deletingPost}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-sm text-red-600 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>{deletingPost ? 'Deleting…' : 'Delete post'}</span>
                            </button>
                          ) : (
                            <button
                              onClick={() => { setReportPostId(post.id); setPostMenuOpen(null) }}
                              className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-red-50 dark:hover:bg-red-900/30 text-sm text-red-600"
                            >
                              <Flag className="w-4 h-4" />
                              <span>Report post</span>
                            </button>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Post Content */}
                <div className="px-4 pb-2">
                  <p className="whitespace-pre-wrap">{post.content}</p>
                  {post.hashtags && post.hashtags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {post.hashtags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => navigate(`/hashtag/${encodeURIComponent(tag.toLowerCase())}`)}
                          className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-full hover:bg-blue-100"
                        >
                          #{tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Post Media */}
                {post.media && post.media.length > 0 && (
                  <div className="mt-2">
                    {post.media[0].mediaType === 'VIDEO' ? (
                      <video
                        src={post.media[0].mediaUrl}
                        controls
                        className="w-full max-h-96 object-cover"
                      />
                    ) : (
                      <img
                        src={post.media[0].mediaUrl}
                        alt="Post media"
                        className="w-full max-h-96 object-cover"
                      />
                    )}
                  </div>
                )}

                {/* Reaction Counts */}
                {(post.likeCount > 0 || post.reactionCounts) && (
                  <div className="px-4 py-2 flex items-center gap-2">
                    <div className="flex -space-x-1">
                      {REACTIONS.filter(r => (post.reactionCounts?.[r.type] || 0) > 0).slice(0, 3).map(r => (
                        <span key={r.type} className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-sm">
                          {r.icon}
                        </span>
                      ))}
                    </div>
                    <span className="text-sm text-muted-foreground">{post.likeCount}</span>
                  </div>
                )}

                {/* Post Stats */}
                <div className="px-4 py-2 text-sm text-muted-foreground flex items-center justify-between">
                  <span>{post.likeCount} likes</span>
                  <span>{post.commentCount} comments · {post.shareCount} shares</span>
                </div>

                {/* Post Actions */}
                <div className="border-t flex items-center justify-around p-1">
                  <div className="relative">
                    <button
                      onClick={() => handleLike(post.id)}
                      onMouseEnter={() => setShowReactionPicker(post.id)}
                      onMouseLeave={() => setShowReactionPicker(null)}
                      className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                        post.isLiked ? 'text-emerald-600' : 'text-muted-foreground hover:bg-muted'
                      )}
                    >
                      <span className="text-lg">{reactionDisplay.icon}</span>
                      <span>{reactionDisplay.label}</span>
                    </button>

                    {/* Reaction Picker */}
                    <AnimatePresence>
                      {showReactionPicker === post.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute bottom-full left-0 mb-2 bg-white shadow-lg rounded-full p-2 flex gap-1 z-20"
                          onMouseEnter={() => setShowReactionPicker(post.id)}
                          onMouseLeave={() => setShowReactionPicker(null)}
                        >
                          {REACTIONS.map(reaction => (
                            <button
                              key={reaction.type}
                              onClick={() => handleReaction(post.id, reaction.type)}
                              className="w-10 h-10 rounded-full hover:bg-muted flex items-center justify-center text-xl transition-transform hover:scale-125"
                              title={reaction.label}
                            >
                              {reaction.icon}
                            </button>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <button
                    onClick={() => handleCommentClick(post.id)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                      activeCommentPost === post.id ? 'text-emerald-600 bg-muted' : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span>Comment</span>
                  </button>

                  {/* Reshare Button */}
                  <button
                    onClick={() => handleReshare(post.id)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                      post.isReshared ? 'text-emerald-600 bg-muted' : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <Repeat className={`w-5 h-5 ${post.isReshared ? 'fill-current' : ''}`} />
                    <span>Reshare{post.reshareCount ? ` · ${post.reshareCount}` : ''}</span>
                  </button>

                  {/* Share Button with Menu */}
                  <div className="relative">
                    <button
                      onClick={() => setShowShareMenu(showShareMenu === post.id ? null : post.id)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-muted-foreground hover:bg-muted transition-colors"
                    >
                      <Share2 className="w-5 h-5" />
                      <span>Share</span>
                    </button>

                    {/* Share Menu */}
                    <AnimatePresence>
                      {showShareMenu === post.id && (
                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: 10 }}
                          className="absolute bottom-full left-0 mb-2 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 min-w-[150px] z-20 border dark:border-gray-700"
                        >
                          <button
                            onClick={() => {
                              handleCopyLink(post.id)
                              handleShare(post.id)
                              setShowShareMenu(null)
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 rounded hover:bg-muted text-sm"
                          >
                            {copiedPostId === post.id ? (
                              <>
                                <Check className="w-4 h-4 text-emerald-500" />
                                <span>Link Copied!</span>
                              </>
                            ) : (
                              <>
                                <Link2 className="w-4 h-4" />
                                <span>Copy Link</span>
                              </>
                            )}
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <button
                    onClick={() => handleSave(post.id)}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 rounded-lg transition-colors',
                      post.isSaved ? 'text-emerald-600' : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <Bookmark className={cn('w-5 h-5', post.isSaved && 'fill-current')} />
                    <span>{post.isSaved ? 'Saved' : 'Save'}</span>
                  </button>
                </div>

                {/* Comments Section */}
                <AnimatePresence>
                  {activeCommentPost === post.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-t p-4"
                    >
                      {/* Comment Input */}
                      <div className="flex gap-2 mb-4">
                        <img
                          src={user?.profileImage || '/default-avatar.svg'}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <input
                          type="text"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                          placeholder="Write a comment..."
                          className="flex-1 px-4 py-2 bg-muted rounded-full focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                        <button
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentText.trim()}
                          className="btn-primary px-4 rounded-full disabled:opacity-50"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Comments List */}
                      {commentsLoading ? (
                        <div className="text-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin mx-auto text-emerald-500" />
                        </div>
                      ) : comments[post.id] && comments[post.id].length > 0 ? (
                        <div className="space-y-3">
                          {comments[post.id].slice(0, 5).map((comment) => (
                            <div key={comment.id}>
                              <div className="flex gap-2">
                                <img
                                  src={comment.user.profileImage || '/default-avatar.svg'}
                                  alt=""
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                                <div className="flex-1 bg-muted rounded-2xl px-4 py-2">
                                  <p className="font-medium text-sm">{comment.user.firstName} {comment.user.lastName}</p>
                                  <p className="text-sm">{comment.content}</p>
                                  <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                                    <span>{formatTimeAgo(comment.createdAt)}</span>
                                    <button
                                      onClick={async () => {
                                        try {
                                          await postService.likeComment(post.id, comment.id)
                                          setComments(prev => ({
                                            ...prev,
                                            [post.id]: prev[post.id].map(c =>
                                              c.id === comment.id
                                                ? { ...c, likeCount: c.likeCount + 1 }
                                                : c
                                            )
                                          }))
                                        } catch (error) {
                                          console.error('Error liking comment:', error)
                                        }
                                      }}
                                      className="hover:text-emerald-600"
                                    >
                                      Like ({comment.likeCount})
                                    </button>
                                    <button
                                      onClick={() => {
                                        const replyText = prompt('Enter your reply:')
                                        if (replyText?.trim()) {
                                          postService.addComment(post.id, replyText, comment.id)
                                            .then(() => fetchComments(post.id))
                                            .catch(err => console.error('Error adding reply:', err))
                                        }
                                      }}
                                      className="hover:text-emerald-600"
                                    >
                                      Reply
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {/* Replies */}
                              {comment.replyCount > 0 && (
                                <div className="ml-10 mt-1">
                                  <button
                                    onClick={() => toggleReplies(comment.id, post.id)}
                                    className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1"
                                  >
                                    {expandedReplies.has(comment.id) ? 'Hide replies' : `View ${comment.replyCount} ${comment.replyCount === 1 ? 'reply' : 'replies'}`}
                                  </button>
                                </div>
                              )}
                              {/* Replies List */}
                              {expandedReplies.has(comment.id) && comment.replies && (
                                <div className="ml-10 mt-2 space-y-2">
                                  {comment.replies.map((reply) => (
                                    <div key={reply.id} className="flex gap-2">
                                      <img
                                        src={reply.user.profileImage || '/default-avatar.svg'}
                                        alt=""
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                      <div className="flex-1 bg-muted/50 rounded-2xl px-3 py-1.5">
                                        <p className="font-medium text-xs">{reply.user.firstName} {reply.user.lastName}</p>
                                        <p className="text-sm">{reply.content}</p>
                                        <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                                          <span>{formatTimeAgo(reply.createdAt)}</span>
                                          <button
                                            onClick={async () => {
                                              try {
                                                await postService.likeComment(post.id, reply.id)
                                                // Update reply like count in state
                                                setComments(prev => ({
                                                  ...prev,
                                                  [post.id]: prev[post.id].map(c =>
                                                    c.id === comment.id
                                                      ? {
                                                          ...c,
                                                          replies: c.replies?.map(r =>
                                                            r.id === reply.id
                                                              ? { ...r, likeCount: r.likeCount + 1 }
                                                              : r
                                                          )
                                                        }
                                                      : c
                                                  )
                                                }))
                                              } catch (error) {
                                                console.error('Error liking reply:', error)
                                              }
                                            }}
                                            className="hover:text-emerald-600"
                                          >
                                            Like ({reply.likeCount})
                                          </button>
                                          <button
                                            onClick={() => {
                                              const replyText = prompt('Enter your reply:')
                                              if (replyText?.trim()) {
                                                postService.addComment(post.id, replyText, comment.id)
                                                  .then(() => fetchComments(post.id))
                                                  .catch(err => console.error('Error adding reply:', err))
                                              }
                                            }}
                                            className="hover:text-emerald-600"
                                          >
                                            Reply
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-center text-muted-foreground text-sm">No comments yet. Be the first to comment!</p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.article>
            )
          })
        )}

        {/* Load More Trigger */}
        <div ref={loadMoreRef} className="py-4">
          {loading && hasMore && (
            <div className="text-center">
              <Loader2 className="w-6 h-6 animate-spin mx-auto text-emerald-500" />
            </div>
          )}
          {!hasMore && posts.length > 0 && (
            <p className="text-center text-muted-foreground text-sm">No more posts</p>
          )}
        </div>
      </div>
    </div>

    {/* Report post modal — shown when a user flags someone else's post */}
    {reportPostId && (
      <ReportModal postId={reportPostId} onClose={() => setReportPostId(null)} />
    )}
    </GovLayout>
  )
}

export default FeedPage